import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Motion";

// Cards render immediately (no scroll-gated reveal) so grids are fully
// present in the DOM on first paint; only images lazy-load below the fold.

export const CategoryCard = ({ cat, index = 0 }) => (
    <Link
      to={`/products/category/${cat.slug}`}
      data-testid={`category-card-${cat.slug}`}
      className="group block border border-line bg-white transition-colors hover:border-accent/40"
    >
      <div className="relative flex h-40 items-center justify-center overflow-hidden border-b border-line bg-mist">
        <img
          src={cat.image} alt={`STJ Innovations ${cat.name} — pharmaceutical cleanroom equipment category`}
          loading={index < 3 ? "eager" : "lazy"} fetchPriority={index < 3 ? "high" : "auto"}
          width="640" height="320"
          className="h-full w-full object-contain p-3 transition-transform duration-500 group-hover:scale-[1.03]"
        />
        <span className="absolute left-3 top-3 bg-ink/80 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 backdrop-blur">
          {String(index + 1).padStart(2, "0")}
        </span>
      </div>
      <div className="p-5">
        <h3 className="font-display text-base font-semibold tracking-tight text-ink transition-colors group-hover:text-accent">
          {cat.name}
        </h3>
        <p className="mt-1.5 text-[13px] leading-relaxed text-steel">{cat.tagline}</p>
        <span className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-accent">
          Explore <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
);

export const ProductCard = ({ product, index = 0 }) => (
    <Link
      to={`/products/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className="group flex h-full flex-col border border-line bg-white transition-colors hover:border-accent/40"
    >
      <div className="relative flex h-40 items-center justify-center overflow-hidden border-b border-line bg-mist">
        <img
          src={product.image} alt={`STJ Innovations ${product.name} — SS 304 cleanroom equipment for pharmaceutical and controlled-environment applications`}
          loading={index < 3 ? "eager" : "lazy"} fetchPriority={index < 3 ? "high" : "auto"}
          width="640" height="320"
          className="h-full w-full object-contain p-3 transition-transform duration-500 group-hover:scale-[1.03]"
        />
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-display text-base font-semibold tracking-tight text-ink transition-colors group-hover:text-accent">
          {product.name}
        </h3>
        <p className="mt-1.5 flex-1 text-[13px] leading-relaxed text-steel line-clamp-3">{product.short_description}</p>
        <span className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-accent">
          View details <ArrowUpRight size={12} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </Link>
);

export const IndustryCard = ({ industry, index = 0 }) => (
    <Link
      to={`/industries/${industry.slug}`}
      data-testid={`industry-card-${industry.slug}`}
      className="group relative block overflow-hidden border border-line bg-navy"
    >
      <div className="spotlight-img relative aspect-[4/3] overflow-hidden">
        <img
          src={industry.image} alt={`${industry.name} industry solutions — STJ Innovations`} loading="lazy"
          className="h-full w-full object-cover opacity-75 transition-all duration-700 group-hover:scale-[1.04] group-hover:opacity-90"
        />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink via-ink/70 to-transparent p-5 pt-16">
          <h3 className="font-display text-lg font-semibold text-white">{industry.name}</h3>
          <span className="mt-1.5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-sky-300">
            View solutions <ArrowRight size={11} className="transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </Link>
);

export const PostCard = ({ post, index = 0 }) => (
    <Link
      to={`/insights/${post.slug}`}
      data-testid={`post-card-${post.slug}`}
      className="group flex h-full flex-col border border-line bg-white transition-colors hover:border-accent/40"
    >
      <div className="spotlight-img relative aspect-[16/9] overflow-hidden bg-navy">
        <img
          src={post.image} alt={post.title} loading="lazy"
          className="h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-[1.04]"
        />
      </div>
      <div className="flex flex-1 flex-col p-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent">{post.category}</p>
        <h3 className="mt-2.5 font-display text-lg font-semibold leading-snug tracking-tight text-ink transition-colors group-hover:text-accent">
          {post.title}
        </h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-steel line-clamp-3">{post.excerpt}</p>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
          {post.author} · {post.date}
        </p>
      </div>
    </Link>
);

export const ProjectCard = ({ project, index = 0 }) => (
    <Link
      to={`/projects/${project.slug}`}
      data-testid={`project-card-${project.slug}`}
      className="group block border border-line bg-white transition-colors hover:border-accent/40"
    >
      <div className="spotlight-img relative aspect-[16/10] overflow-hidden bg-navy">
        <img
          src={project.image} alt={project.title} loading="lazy"
          className="h-full w-full object-cover opacity-85 transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <span className="absolute left-4 top-4 bg-ink/80 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 backdrop-blur">
          {project.industry}
        </span>
      </div>
      <div className="p-6">
        <h3 className="font-display text-lg font-semibold leading-snug tracking-tight text-ink transition-colors group-hover:text-accent">
          {project.title}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-steel line-clamp-2">{project.application}</p>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
          {project.location} · {project.status}
        </p>
      </div>
    </Link>
);
